import { ArrowLeft, Clock3, Copy, History, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ReasonResultDetail, ReasonSegment } from '../types';
import { getPreviewUrl } from '../types';
import { ReasonSegmentPlayer } from './ReasonSegmentPlayer';

interface ReasonResultViewerProps {
  result: ReasonResultDetail;
  segmentIndex: number;
  loading: boolean;
  onBack: () => void;
  onRevisionChange: (revision: number) => void;
  onSegmentChange: (index: number) => void;
}

function formatDate(value: number): string {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(value);
}

function formatDuration(value: number): string {
  const totalSeconds = Math.max(0, Math.round(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export function ReasonResultViewer({
  result,
  segmentIndex,
  loading,
  onBack,
  onRevisionChange,
  onSegmentChange,
}: ReasonResultViewerProps) {
  const [copied, setCopied] = useState(false);
  const segment = result.segments[segmentIndex];
  const playerSegment = useMemo<ReasonSegment | null>(
    () =>
      segment
        ? {
            segment_index: segment.segment_index,
            start_frame: segment.start_frame,
            end_frame_exclusive: segment.end_frame_exclusive,
            start_time_s: segment.start_time_s,
            end_time_s: segment.end_time_s,
            reason_code: segment.reasons[0]?.reason_code ?? null,
            reason_codes: segment.reasons.map((reason) => reason.reason_code),
          }
        : null,
    [segment]
  );
  const latestRevision = result.revisions[0]?.revision ?? result.revision;

  return (
    <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-brand-border pb-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to Results"
            title="Back to Results"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary hover:text-brand-text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate font-mono text-base font-semibold text-brand-text-primary">
                {result.episode_id}
              </h1>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(result.episode_id).then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1_500);
                  });
                }}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-brand-border px-2 text-xs text-brand-text-secondary"
              >
                <Copy className="h-3.5 w-3.5" /> {copied ? 'Copied' : 'Copy ID'}
              </button>
            </div>
            <p className="mt-1 text-xs text-brand-text-secondary">
              {result.operator} · completed {formatDate(result.completed_at_ms)} · V
              {result.submission_version}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-brand-text-secondary">
            <History className="h-4 w-4" /> Revision
            <select
              value={result.revision}
              disabled={loading}
              onChange={(event) => onRevisionChange(Number(event.target.value))}
              className="h-9 rounded-md border border-brand-border bg-brand-surface px-3 text-sm text-brand-text-primary"
            >
              {result.revisions.map((revision) => (
                <option key={revision.revision} value={revision.revision}>
                  {revision.revision} · {revision.operator} · {formatDate(revision.completed_at_ms)}
                </option>
              ))}
            </select>
          </label>
          <span className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs text-brand-text-secondary">
            <ShieldCheck className="h-4 w-4" /> {result.ddb_sync_status}
          </span>
        </div>
      </div>

      {result.revision !== latestRevision ? (
        <div className="mt-4 border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Viewing historical revision {result.revision}. Latest revision is {latestRevision}.
        </div>
      ) : null}
      {result.source_changed ? (
        <div className="mt-4 border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Source changed after this submission.
        </div>
      ) : null}

      <section className="mt-5 grid grid-cols-2 gap-px border border-brand-border bg-brand-border sm:grid-cols-4 lg:grid-cols-8">
        <div className="bg-brand-surface px-3 py-3">
          <div className="text-[11px] uppercase text-brand-text-secondary">Segments</div>
          <div className="mt-1 font-mono text-sm">{result.segment_count}</div>
        </div>
        <div className="bg-brand-surface px-3 py-3">
          <div className="text-[11px] uppercase text-brand-text-secondary">Reasons</div>
          <div className="mt-1 font-mono text-sm">{result.reason_selection_count}</div>
        </div>
        <div className="bg-brand-surface px-3 py-3">
          <div className="text-[11px] uppercase text-brand-text-secondary">Fail duration</div>
          <div className="mt-1 font-mono text-sm">{formatDuration(result.output_duration_s)}</div>
        </div>
        <div className="bg-brand-surface px-3 py-3">
          <div className="text-[11px] uppercase text-brand-text-secondary">Handling</div>
          <div className="mt-1 font-mono text-sm">
            {formatDuration(result.handling_elapsed_ms / 1_000)}
          </div>
        </div>
        <div className="bg-brand-surface px-3 py-3 sm:col-span-2">
          <div className="text-[11px] uppercase text-brand-text-secondary">Environment</div>
          <div className="mt-1 truncate font-mono text-xs" title={result.worker_version}>
            {result.environment} · {result.worker_version}
          </div>
        </div>
        <div className="bg-brand-surface px-3 py-3 sm:col-span-2">
          <div className="text-[11px] uppercase text-brand-text-secondary">Source hash</div>
          <div className="mt-1 truncate font-mono text-xs" title={result.source_audit_hash}>
            {result.source_audit_hash}
          </div>
        </div>
      </section>

      {segment && playerSegment ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="min-w-0">
            <ReasonSegmentPlayer
              src={getPreviewUrl({ episode_id: result.episode_id })}
              segment={playerSegment}
              segmentIndex={segmentIndex}
              segmentCount={result.segments.length}
              onPrevious={() => onSegmentChange(segmentIndex - 1)}
              onNext={() => onSegmentChange(segmentIndex + 1)}
            />
            <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-2">
              {result.segments.map((item, index) => (
                <button
                  type="button"
                  key={item.segment_index}
                  onClick={() => onSegmentChange(index)}
                  aria-label={`Open Segment ${index + 1}`}
                  className={`h-10 rounded-md border font-mono text-xs ${
                    index === segmentIndex
                      ? 'border-brand-orange bg-brand-orange/10 text-brand-orange'
                      : 'border-brand-border text-brand-text-secondary hover:text-brand-text-primary'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>

          <aside className="self-start border border-brand-border bg-brand-surface p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-brand-text-primary">
                Segment {segmentIndex + 1}
              </h2>
              <span className="inline-flex items-center gap-1.5 font-mono text-xs text-brand-text-secondary">
                <Clock3 className="h-3.5 w-3.5" /> {segment.start_time_s.toFixed(2)}–
                {segment.end_time_s.toFixed(2)}s
              </span>
            </div>
            <div className="mt-5 space-y-3">
              {segment.reasons.map((reason) => (
                <div
                  key={`${reason.reason_code}:${reason.reason_note ?? ''}`}
                  className="border-t border-brand-border pt-3 first:border-t-0 first:pt-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md border border-brand-orange/50 bg-brand-orange/10 px-2 py-1 text-sm text-brand-text-primary">
                      {reason.reason_label}
                    </span>
                    <span className="font-mono text-[11px] text-brand-text-secondary">
                      {reason.reason_code} · V{reason.taxonomy_version}
                    </span>
                    {!reason.enabled_now ? (
                      <span className="rounded border border-amber-500/50 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">
                        Historical
                      </span>
                    ) : null}
                  </div>
                  {reason.reason_note ? (
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-brand-text-secondary">
                      {reason.reason_note}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </aside>
        </div>
      ) : (
        <div className="mt-6 border border-brand-border px-4 py-16 text-center text-sm text-brand-text-secondary">
          This revision contains no fail segments.
        </div>
      )}
    </main>
  );
}
